import { useMutation } from "@tanstack/react-query"
import { trpc } from "app/trpc"
import { Value } from "@stringtale/react"
import { useCurrentUser } from "app/core/hooks/useCurrentUser"
import { usePosthogWithCurrentUser } from "app/core/hooks/usePosthogWithCurrentUser"
import useToast from "app/toasts/hooks/useToast"
import { cn } from "app/lib/utils"
import { getPlayerThickIcon } from "app/player/getPlayerThickIcon"
import { currentOpenPopoverAtom } from "app/player/state"
import { FileVariantType, UserRole } from "db/enums"
import { useAtom } from "jotai"
import { Popover } from "radix-ui"
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
} from "react"
import { poppins } from "theme/fonts"
import { getVideoPlayerPortalContainer } from "../../utils/getVideoPlayerPortalContainer"
import PlayerIcon from "./PlayerIcon"
import PlayerIconButton from "./PlayerIconButton"

const PlayerVideoIssueModal = ({
  videoId,
  selectedQuality,
  size,
  color = "blue",
}: {
  videoId: number
  selectedQuality: FileVariantType
  size?: ComponentProps<typeof PlayerIconButton>["size"]
  color?: ComponentProps<typeof PlayerIconButton>["color"]
}) => {
  const user = useCurrentUser()

  const [openMenu, setOpenMenu] = useAtom(currentOpenPopoverAtom)
  const isOpen = openMenu === "video-issue"
  const [emailSent, setEmailSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const addToast = useToast()
  const newIssueMutation = useMutation(trpc.player.newIssue.mutationOptions())
  const { isPending: isMutationLoading } = newIssueMutation

  const analytics = usePosthogWithCurrentUser(user)
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearResetTimeout = useCallback(() => {
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current)
      resetTimeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => clearResetTimeout()
  }, [clearResetTimeout])

  const handleButtonClick = async () => {
    setIsLoading(true)

    try {
      await newIssueMutation.mutateAsync({ videoId, selectedQuality })
      setEmailSent(true)
      analytics.capture({
        name: "video_issue_email_sent",
        properties: {
          videoId: videoId,
          selectedQuality: selectedQuality,
        },
      })
      addToast({
        content: (
          <Value name="apps.web.src.video-slides.components.player.playervideoissuemodal.email_verstuurd_toast">
            Instructie mail is verstuurd.
          </Value>
        ),
      })

      clearResetTimeout()
      resetTimeoutRef.current = setTimeout(() => {
        resetTimeoutRef.current = null
        setOpenMenu((prev) => (prev === "video-issue" ? null : prev))
        setIsLoading(false)
        setEmailSent(false)
      }, 3000)
    } catch {
      setIsLoading(false)
    }
  }

  const onOpenChange = useCallback(
    (val: boolean) => {
      if (val) {
        analytics.capture({
          name: "video_issue_popover_opened",
          properties: {
            videoId: videoId,
            selectedQuality: selectedQuality,
          },
        })
        setOpenMenu("video-issue")
      } else {
        clearResetTimeout()
        analytics.capture({
          name: "video_issue_popover_closed",
          properties: {
            videoId: videoId,
            selectedQuality: selectedQuality,
            automatic: emailSent,
          },
        })
        setEmailSent(false)
        setIsLoading(false)
        setOpenMenu((prev) => (prev === "video-issue" ? null : prev))
      }
    },
    [
      analytics,
      videoId,
      selectedQuality,
      emailSent,
      setOpenMenu,
      clearResetTimeout,
    ]
  )

  if (!user || (user.role !== UserRole.USER && user.role !== UserRole.ADMIN)) {
    return null
  }

  const isButtonDisabled = isMutationLoading || isLoading || emailSent

  return (
    <Popover.Root open={isOpen} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <PlayerIconButton size={size} color={color} toggled={isOpen}>
          <PlayerIcon
            src={
              isOpen
                ? "/icons/player/thick/ico_flag_active.svg"
                : getPlayerThickIcon(color ?? "blue", "ico_flag.svg")
            }
            size={18}
          />
        </PlayerIconButton>
      </Popover.Trigger>
      <Popover.Portal container={getVideoPlayerPortalContainer()}>
        <Popover.Content
          align="end"
          side="top"
          sideOffset={4}
          className={cn(
            "z-50 flex w-[270px] flex-col items-start rounded-lg border border-black bg-neutraal-950 p-1 text-base shadow-[0_0_4px_rgba(0,0,0,0.25)] outline-none",
            poppins.className
          )}
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          <div className="flex w-full items-center border-b border-white/40 px-3 py-2">
            <div className="text-2xs font-medium tracking-wide text-white uppercase opacity-80">
              <Value name="apps.web.src.video-slides.components.player.playervideoissuemodal.report_video_issue">
                Problemen met video&apos;s
              </Value>
            </div>
          </div>
          <div className="flex w-full flex-col gap-3 p-3">
            <p className="m-0 text-md leading-sm font-normal text-white">
              <Value name="apps.web.src.video-slides.components.player.playervideoissuemodal.ervaar_je_problemen">
                Ervaar je problemen de video? Ontvang een stappenplan per mail.
              </Value>
            </p>
            <button
              type="button"
              disabled={isButtonDisabled}
              className={cn(
                "flex h-12 w-full items-center justify-center rounded px-3 text-md font-normal text-white transition-colors disabled:cursor-not-allowed",
                emailSent
                  ? "bg-[linear-gradient(rgba(255,255,255,0.2),rgba(255,255,255,0.2)),linear-gradient(#005422,#005422)]"
                  : "border border-white bg-white/10 hover:bg-white/20 active:bg-white active:text-neutraal-950 disabled:opacity-80"
              )}
              onClick={handleButtonClick}
            >
              {emailSent ? (
                <span className="inline-flex items-center gap-1.5">
                  <Value name="apps.web.src.video-slides.components.player.playervideoissuemodal.verstuurd">
                    Verstuurd
                  </Value>
                  <PlayerIcon
                    src="/icons/player/thick/ico_check_thin_white.svg"
                    size={24}
                  />
                </span>
              ) : isLoading ? (
                <Value name="apps.web.src.video-slides.components.player.playervideoissuemodal.even_geduld">
                  Even geduld
                </Value>
              ) : (
                <Value name="apps.web.src.video-slides.components.player.playervideoissuemodal.stuur_email">
                  Stuur email
                </Value>
              )}
            </button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

export default PlayerVideoIssueModal
